import { useMemo } from 'react';
import type { JSX } from 'react';

import { useLocation } from 'react-router';

import { Text } from '@change/ds-core-ui/Text';
import type { TextProps } from '@change/ds-core-ui/Text';
import { cn } from '@change/ds-utils';

import { memo } from '@change-corgi/core-react/core';
import { parseHtml } from '@change-corgi/core-react/html';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { ALLOWED_IFRAME_ATTRS, RESTRICTED_ATTRS, RESTRICTED_TAGS } from './constants';
import { genReplaceTags } from './genReplaceTags';
import type { ReplaceTagsOptions } from './genReplaceTags';

/**
 * TextProps (not ComponentPropsWithRef<typeof Text>) - Text is a generic/polymorphic
 * component, and deriving its props via ComponentPropsWithRef + Omit silently collapses
 * every prop (size, variant, ...) to `any`, defeating type-checking at every call site.
 * 'as' is omitted too so it stays the fully generic React.ElementType declared below,
 * rather than narrowing to the 'div' placeholder used for TextProps' own element param.
 */
type Props = Omit<TextProps<'div'>, 'as' | 'variant' | 'children' | 'ellipsis'> &
	ReplaceTagsOptions & {
		html: string;
		as?: React.ElementType;
	};

// Content styles applied to the parsed HTML (previously a styled(Text) emotion block).
// Expressed as design-system Tailwind arbitrary variants so we no longer depend on emotion.
const CONTENT_CLASSES = cn(
	'[&_ol]:mx-[3em] [&_ol]:my-[1.5em] [&_ul]:mx-[3em] [&_ul]:my-[1.5em]',
	'[&_ol]:list-decimal [&_ul]:list-[circle]',
	'[&_ol>li]:mx-[0.5em] [&_ol>li]:my-[0.75em] [&_ul>li]:mx-[0.5em] [&_ul>li]:my-[0.75em]',
	'[&_p+p]:mt-[1.5em]',
	'[&_iframe]:max-w-full [&_img]:max-w-full [&_video]:max-w-full',
);

export const Html = memo(
	({
		html,
		onClickLink,
		linkVariant,
		stripLinks,
		supportHashLinks,
		linksTarget,
		transformLinkHref,
		reportBadLinks,
		reportParams,
		as,
		transformers,
		noRestrictedTags,
		noRestrictedAttrs,
		noContentStyles,
		allowIframes,
		className,
		breakWord,
		ref,
		expressive,
		...rest
	}: Props & { ref?: React.RefObject<HTMLDivElement> }): JSX.Element => {
		const location = useLocation();
		const utilityContext = useUtilityContext();

		const { restrictedTags, restrictedAttrs, allowedTags, allowedAttrs } = useMemo(() => {
			const tags = noRestrictedTags ? undefined : RESTRICTED_TAGS;
			const attrs = noRestrictedAttrs ? undefined : RESTRICTED_ATTRS;
			const allowed = allowIframes ? { tags: ['iframe'], attrs: ALLOWED_IFRAME_ATTRS } : undefined;
			if (!transformers) {
				return {
					restrictedAttrs: attrs,
					restrictedTags: tags,
					allowedTags: allowed?.tags,
					allowedAttrs: allowed?.attrs,
				};
			}
			return {
				restrictedTags: !tags
					? undefined
					: ([] as string[]).concat(tags, ...transformers.map((transformer) => transformer.transformedTags || [])),
				restrictedAttrs: !attrs
					? undefined
					: ([] as string[]).concat(attrs, ...transformers.map((transformer) => transformer.transformedAttrs || [])),
				allowedTags: allowed?.tags,
				allowedAttrs: allowed?.attrs,
			};
		}, [transformers, noRestrictedTags, noRestrictedAttrs, allowIframes]);

		const replace = useMemo(
			() =>
				genReplaceTags(
					{
						onClickLink,
						linkVariant,
						expressive,
						stripLinks,
						reportBadLinks,
						supportHashLinks,
						linksTarget,
						transformLinkHref,
						reportParams,
					},
					location,
					utilityContext,
				),
			[
				onClickLink,
				linkVariant,
				expressive,
				stripLinks,
				reportBadLinks,
				supportHashLinks,
				linksTarget,
				transformLinkHref,
				reportParams,
				location,
				utilityContext,
			],
		);

		const Container = noContentStyles ? 'div' : Text;

		return (
			<Container
				as={as || 'div'}
				{...rest}
				expressive={expressive}
				ref={ref}
				className={cn(!noContentStyles && CONTENT_CLASSES, className, {
					'wrap-break-word': breakWord,
				})}
			>
				{parseHtml(html, {
					replace,
					restrictedTags,
					restrictedAttrs,
					allowedTags,
					allowedAttrs,
					transformers,
				})}
			</Container>
		);
	},
);
